import { useEffect, useState } from 'react';
import { LoadingCard } from '@picsart/design-system/components/LoadingCard';
import { Progress } from '@picsart/design-system/components/Progress';
import { useTranslator } from '@picsart/rc/services/localization';
import useStyles from './styles';
import { useAppStore, type IAppStore } from '@/store';
import { PreviewMediaType } from '@/types';
import {
  LOADING_STEP_TITLE,
  LOADING_STEP_TEXT_IMAGE,
  LOADING_STEP_TEXT_VIDEO,
  LOADING_CARD_TITLE,
  LOADING_CARD_TEXT_IMAGE,
  LOADING_CARD_TEXT_VIDEO,
} from '@/consts/content';
import {
  FINAL_PERCENT,
  GENERATION_DURATION_SECONDS,
} from '@/consts/generation';

import { LoadingVariant, type LoadingProps } from './types';

const selector = (store: IAppStore) => ({
  selectedPreset: store.selectedPreset,
  generationProgress: store.generationProgress,
  setGenerationProgress: store.setGenerationProgress,
});

const Loading = ({
  variant = LoadingVariant.Default,
  startTime,
  mediaTypeOverride,
}: LoadingProps = {}) => {
  const classes = useStyles();
  const translate = useTranslator();
  const { selectedPreset, generationProgress, setGenerationProgress } =
    useAppStore(selector);

  const [localProgress, setLocalProgress] = useState(0);

  // Per-card timing is the canonical source. The previous fallback to the
  // store's generationStartTime read a slice field that was never written;
  // dropping it preserves the old (frozen-progress) ResultOverlay behavior.
  const effectiveStartTime = startTime;
  const hasLocalTiming = startTime !== undefined;

  const mediaType =
    mediaTypeOverride ??
    (selectedPreset?.type === PreviewMediaType.VIDEO ? 'video' : 'image');

  // Use compact text for compact variant (inline cards and mobile full-screen)
  const isCompact = variant === LoadingVariant.Compact;
  const title = isCompact ? LOADING_CARD_TITLE : LOADING_STEP_TITLE;
  const description = isCompact
    ? mediaType === 'video'
      ? LOADING_CARD_TEXT_VIDEO
      : LOADING_CARD_TEXT_IMAGE
    : mediaType === 'video'
    ? LOADING_STEP_TEXT_VIDEO
    : LOADING_STEP_TEXT_IMAGE;

  // Calculate progress based on elapsed time from generation start
  useEffect(() => {
    if (!effectiveStartTime) return;

    const durationKey = mediaType === 'video' ? 'video' : 'image';
    const totalDuration = GENERATION_DURATION_SECONDS[durationKey] * 1000;

    const updateProgress = () => {
      const elapsed = Date.now() - effectiveStartTime;
      const newProgress = Math.min(
        (elapsed / totalDuration) * FINAL_PERCENT,
        FINAL_PERCENT
      );
      // Only update if not already at 100%
      if (newProgress < FINAL_PERCENT) {
        if (hasLocalTiming) {
          setLocalProgress(newProgress);
        } else {
          setGenerationProgress(newProgress);
        }
      }
    };

    // Update immediately on mount to restore progress
    updateProgress();

    const intervalId = setInterval(updateProgress, 1000);
    return () => clearInterval(intervalId);
  }, [mediaType, effectiveStartTime, hasLocalTiming, setGenerationProgress]);

  const percent = hasLocalTiming ? localProgress : generationProgress;

  // Compact variant for inline card display: the content is scaled down in
  // CSS to fit the square card, so it matches regular grid items.
  if (isCompact) {
    return (
      <div className={classes.compactWrapper}>
        <div className={classes.compactFitBox}>
          <div className={classes.compactContent}>
            <LoadingCard
              className={classes.loadingCard}
              mediaType={mediaType}
              title={translate(title)}
              description={translate(description)}
            />
            <div className={classes.compactProgress}>
              <Progress percent={percent} variant="linear" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.wrapper}>
        <div className={classes.contentWrapper}>
          <div className={classes.loadingCard}>
            <LoadingCard
              mediaType={mediaType}
              title={translate(title)}
              description={translate(description)}
            />
            <Progress percent={percent} variant="linear" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Loading;
